import { CloseOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon } from 'assets/images/svg';
import { constants } from 'config/constants';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentialAccessByDeviceID, selectCredentials, selectLoadingCredentialIDs } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Space, Tag, Typography } from 'zui/Antd';

const { Title, Text } = Typography;
const { SMART_LOCKS, ZEZAMII_ACCESS } = constants;

const ManageDeviceCredentialsModal = ({ visible: visibleProp, setVisible: setVisibleProp, open, setOpen, deviceID }) => {
	const dispatch = useDispatch();
	const {
		paddingLG,
		paddingSM,
		paddingXS,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		paddingXXS
	} = theme.useToken().token;

	// ✅ Redux selectors
	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const credentialAccess = useSelector((state) => selectCredentialAccessByDeviceID(state, deviceID));
	const credentials = useSelector(selectCredentials);
	const people = useSelector(selectPeople);
	const loadingCredentialIDs = useSelector(selectLoadingCredentialIDs);
	const loadingUpdateCredential = Object.keys(loadingCredentialIDs).length > 0;

	// ✅ Determine integration type based on device category
	const deviceIntegrationID = useMemo(() => {
		if (!device) return null;
		if (device.defaultCategoryID === ZEZAMII_ACCESS) return INTEGRATIONS_TYPES.INTEGRATION_CONTROL.id;
		if (device.defaultCategoryID === SMART_LOCKS) return INTEGRATIONS_TYPES.INTEGRATION_TTLOCK.id;
		return null;
	}, [device]);

	// ✅ Local state
	const [newAccess, setNewAccess] = useState([]);
	const [removedAccess, setRemovedAccess] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [selectedPersonID, setSelectedPersonID] = useState(null);
	const [selectedCredentialID, setSelectedCredentialID] = useState(null);

	const visible = visibleProp ?? open ?? false;
	const setVisible = setVisibleProp ?? setOpen;

	// ✅ Available credentials for this device (Control or TTLock based on device category)
	const availableCredentials = useMemo(() => {
		if (!deviceIntegrationID) return [];

		// 🔒 Get all credentialIDs that exist in Device.Cards for THIS device
		const deviceCredentialIDs = new Set(credentialAccess.map((a) => a.credentialID));

		const assignedCredentialIDs = new Set([
			...credentialAccess.filter((a) => a.personID !== null).map((a) => a.credentialID),
			...newAccess.map((a) => a.credentialID)
		]);
		const removedCredentialIDs = new Set(removedAccess.map((a) => a.credentialID));

		return credentials
			.filter(
				(c) =>
					c.integrationID === deviceIntegrationID &&
					deviceCredentialIDs.has(c.credentialID) && // 🔒 Only credentials that exist in Device.Cards for this device
					!assignedCredentialIDs.has(c.credentialID) &&
					!removedCredentialIDs.has(c.credentialID)
			)
			.map((c) => ({
				label: c.name,
				value: c.credentialID
			}));
	}, [deviceIntegrationID, credentials, credentialAccess, newAccess, removedAccess]);

	// ✅ Available people (not yet assigned to this device)
	const availablePeople = useMemo(() => {
		const assignedPersonIDs = new Set([
			...credentialAccess.filter((a) => a.personID !== null).map((a) => a.personID), // 🔒 Only assigned people
			...newAccess.map((a) => a.personID)
		]);

		return people
			.filter((p) => !assignedPersonIDs.has(p.personID))
			.map((p) => ({
				label: p.displayName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown',
				value: p.personID
			}));
	}, [people, credentialAccess, newAccess]);

	// ✅ Handle cancel and reset changes
	const handleCancel = () => {
		setNewAccess([]);
		setRemovedAccess([]);
		setHasChanges(false);
		setSelectedPersonID(null);
		setSelectedCredentialID(null);
		setVisible?.(false);
	};

	// ✅ Add new assignment
	const handleAddAssignment = () => {
		setNewAccess([...newAccess, { personID: selectedPersonID, credentialID: selectedCredentialID }]);
		setSelectedPersonID(null);
		setSelectedCredentialID(null);
		setHasChanges(true);
	};

	// ✅ Remove existing assignment
	const handleRemove = (credentialID, personID) => {
		const isNewAssignment = newAccess.some((a) => a.credentialID === credentialID && a.personID === personID);

		if (isNewAssignment) {
			// Remove from newAccess
			setNewAccess(newAccess.filter((a) => !(a.credentialID === credentialID && a.personID === personID)));
		} else {
			// Add to removedAccess
			setRemovedAccess([...removedAccess, { credentialID, personID }]);
		}
		setHasChanges(true);
	};

	// ✅ Submit changes
	const handleSubmit = () => {
		// Assign new
		newAccess.forEach(({ credentialID, personID }) => {
			dispatch(
				credentialsActions.assignCredential({ credentialID, data: { newPersonID: personID } })
			);
		});

		// Unassign removed
		removedAccess.forEach(({ credentialID, personID }) => {
			dispatch(
				credentialsActions.unassignCredential({ credentialID, data: { oldPersonID: personID } })
			);
		});

		handleCancel();
	};

	// ✅ Get display items (existing + new - removed)
	const displayItems = useMemo(() => {
		const existing = credentialAccess
			.filter((a) => a.personID !== null) // 🔒 Only show cards with assigned person
			.filter((a) => !removedAccess.some((r) => r.credentialID === a.credentialID && r.personID === a.personID))
			.map((a) => ({ ...a, isExisting: true }));

		const newItems = newAccess.map((a) => ({ ...a, isNew: true }));

		return [...existing, ...newItems];
	}, [credentialAccess, newAccess, removedAccess]);

	return (
		<Modal
			open={visible}
			onCancel={handleCancel}
			closable
			centered
			destroyOnClose
			width={763}
			footer={[
				<>
					<Divider key='divider' />
					<Flex justify='flex-end' gap={paddingSM} key='footer'>
						<Button onClick={handleCancel} type='default' disabled={loadingUpdateCredential}>
							Cancel
						</Button>
						<Button
							onClick={handleSubmit}
							type='primary'
							loading={loadingUpdateCredential}
							disabled={!hasChanges}
						>
							Save Changes
						</Button>
					</Flex>
				</>
			]}
			styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
		>
			<Row gutter={[paddingLG, paddingLG]} style={{ paddingBottom: paddingLG }} align='middle'>
				<Col span={24}>
					<Title level={4} style={{ margin: 0 }}>
						Manage Credentials
					</Title>
					<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
						Assign cards to people for this device.
					</Text>
				</Col>

				<Col span={24}>
					<Flex vertical gap={paddingXS}>
						<Text strong>Assign Card Access</Text>
						<Select
							placeholder='Select Person'
							options={availablePeople}
							value={selectedPersonID}
							onChange={setSelectedPersonID}
							showSearch
							filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
							style={{ width: '100%' }}
						/>
						<Select
							placeholder='Select Card'
							options={availableCredentials}
							value={selectedCredentialID}
							onChange={setSelectedCredentialID}
							showSearch
							filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
							disabled={!selectedPersonID}
							style={{ width: '100%' }}
						/>
						<Button
							type='dashed'
							disabled={!selectedPersonID || !selectedCredentialID}
							onClick={handleAddAssignment}
							style={{ width: '100%' }}
						>
							Add Assignment
						</Button>
					</Flex>
				</Col>

				<Col span={24}>
					<Flex vertical gap={paddingSM}>
						<Text strong>Credential</Text>
						{displayItems.length === 0 ? (
							<Flex
								justify='center'
								align='center'
								style={{
									width: '100%'
								}}
							>
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description='No credential access. Add credential access using the form above or sync from Control integration.'
								/>
							</Flex>
						) : (
							<Space wrap size={paddingSM}>
								{displayItems.map((a) => {
									const credential = credentials.find((c) => c.credentialID === a.credentialID);
									const person = people.find((p) => p.personID === a.personID);
									const personName =
										person?.displayName ||
										`${person?.firstName || ''} ${person?.lastName || ''}`.trim() ||
										'Unassigned';

									return (
										<Tag
											key={`${a.credentialID}-${a.personID}`}
											color={a.isNew ? 'green' : 'magenta'}
											style={{
												fontWeight: fontWeightStrong,
												borderColor: 'currentColor',
												marginInlineEnd: 0
											}}
										>
											<Flex align='center' gap={paddingXXS}>
												<CreditCardIcon color='currentColor' />
												{`${credential?.name || 'Unknown Card'} — ${personName}`}
												<CloseOutlined
													style={{ cursor: 'pointer' }}
													onClick={() => handleRemove(a.credentialID, a.personID)}
												/>
											</Flex>
										</Tag>
									);
								})}
							</Space>
						)}
					</Flex>
				</Col>
			</Row>
		</Modal>
	);
};

export default ManageDeviceCredentialsModal;
